import { useState, useRef, useCallback } from 'react';
import { Upload, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import ImageCropper from './ImageCropper';
import { uploadApi } from '@/services/uploadApi';
import {
  getFileTypeError,
  getFileExtension,
  getCroppedImage,
  compressImageIfNeeded,
  uploadToS3WithRetry,
} from '@/utils/imageUtils';
import type { ActivityImageValue, CroppedAreaPixels } from '@/types/upload';

interface CustomImageUploadProps {
  isActive: boolean;
  onActivate: () => void;
  onUpload: (value: ActivityImageValue) => void;
  warning?: string | null;
}

export default function CustomImageUpload({
  isActive,
  onActivate,
  onUpload,
  warning,
}: CustomImageUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const error = getFileTypeError(file);
    if (error) {
      toast.error(error);
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result as string);
    reader.readAsDataURL(file);
  }, []);

  const handleCropComplete = useCallback(
    async (croppedAreaPixels: CroppedAreaPixels) => {
      if (!imageSrc || !selectedFile) return;

      try {
        setIsUploading(true);

        const croppedBlob = await getCroppedImage(imageSrc, croppedAreaPixels);
        const finalBlob = await compressImageIfNeeded(croppedBlob);
        const fileType = getFileExtension(selectedFile.type);

        const document = await uploadApi.createDocument({
          fileType,
          mime: 'image/jpeg',
        });

        await uploadToS3WithRetry(document.uploadUrl, finalBlob);
        await uploadApi.markAsUploaded({
          documentId: parseInt(document.id, 10),
        });

        const value: ActivityImageValue = {
          type: 'custom',
          documentId: parseInt(document.id, 10),
          viewUrl: document.viewUrl,
        };

        onUpload(value);

        setImageSrc(null);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';

        toast.success('Image uploaded successfully');
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(error instanceof Error ? error.message : 'Failed to upload image');
      } finally {
        setIsUploading(false);
      }
    },
    [imageSrc, selectedFile, onUpload]
  );

  return (
    <>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onActivate}
            className="w-5 h-5 flex items-center justify-center cursor-pointer"
          >
            {isActive ? (
              <Check className="w-4 h-4 text-admin-accent-blue" />
            ) : (
              <span className="w-4 h-4 rounded-sm border border-admin-slate-300/30 hover:border-admin-slate-300/60 transition-colors" />
            )}
          </button>
          <span className="text-sm font-medium text-admin-slate-400">Custom Image Upload</span>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />

        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="w-full h-20 border-dashed ml-7"
        >
          {isUploading ? (
            <div className="flex flex-col items-center gap-1">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-xs">Uploading...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Upload className="w-6 h-6" />
              <span className="text-xs">Upload image</span>
              <span className="text-xs text-admin-slate-500">382×197px</span>
            </div>
          )}
        </Button>

        {warning && !isActive && (
          <p className="text-xs text-amber-400 ml-7">{warning}</p>
        )}
      </div>

      {imageSrc && (
        <ImageCropper
          imageSrc={imageSrc}
          onCropComplete={handleCropComplete}
          onCancel={() => {
            setImageSrc(null);
            setSelectedFile(null);
          }}
        />
      )}
    </>
  );
}
